<script setup lang="ts">
import { useFormValidation } from "../../../../composables/useFormValidation";
import { required } from "../../../../utils/validation";
import { api } from "../../../../api/Api";
import Toast from "../../../../services/Toast";
import { Permission } from "../../../../types/auth";
import { CashierDuty, Currency, Customer, GeneralLedger, InvoiceList, PaymentType, PosStation } from "../../../../types/models";
import { ref, onMounted, watch, computed } from "vue";
import { useScreenSize } from "../../../../utils/useScreenSize";
import {
  documentActions,
  downloadPDFBlob,
  formatDate,
  formatNumber,
  isEmpty,
  printPDFBlob,
  sharePDFBlob,
} from "../../../../utils/CommonFunctions";
import MessageBox from "../../../../services/MessageBox";
import DocumentHeader from "../../../../components/ui/Templates/DocumentHeader.vue";
import SearchSelect from "../../../../components/ui/form/SearchSelect.vue";
import TabComponent from "../../../../components/ui/form/TabComponent.vue";
import AccountingLog from "../../../../components/ui/Templates/AccountingLog.vue";
import RadioCheckInput from "../../../../components/ui/form/RadioCheckInput.vue";
import DocumentFooter from "../../../../components/ui/Templates/DocumentFooter.vue";
import AmountInput from "../../../../components/ui/form/AmountInput.vue";
import ModalFormComponent from "../../../../components/ui/common/ModalFormComponent.vue";
import TextInput from "../../../../components/ui/form/TextInput.vue";
import FileUpload from "../../../../components/ui/form/FileUpload.vue";
import TextAreaInput from "../../../../components/ui/form/TextAreaInput.vue";
import Buttons from "../../../../components/ui/form/Buttons.vue";
import { tokenManager } from "../../../../api/utils/tokenManager";

const props = defineProps<{
  data?: any | null;
  mode: "create" | "edit" | "view" | "showQuickView" | "toggleStatus";
  title: string | null;
  endPoint: string;
  permissions?: Permission[];
  listCreateName?: string;
  lists?: any;
  posStation?: PosStation | undefined
  cashierDuty?: CashierDuty | null
}>();

const emit = defineEmits<{
  (e: "save", data: any): void;
  (e: "close"): void;
}>();

const { isMobile } = useScreenSize();

const {
  fields,
  formData,
  setFieldRules,
  validateForm,
  resetForm,
  setFormData,
} = useFormValidation({
  date_time: props.data?.date_time || formatDate(new Date()),
  prefix: props.data?.prefix || "",
  suffix: props.data?.suffix || "",
  document_no: props.data?.document_no || "",
  amount: props.data?.amount || "",
  outlets_id: props.data?.outlets_id || "",
  payment_types_id: props.data?.payment_types_id || "",
  chart_of_accounts_id: props.data?.chart_of_accounts_id || "",
  outlet_pos_stations_id: props.data?.outlet_pos_stations_id || "",
  currencys_id: props.data?.currencys_id || 1,
  exchange_rate: props.data?.exchange_rate || 1,
  cashier_shifts_id: props.data?.cashier_shifts_id || "",
  customers_id: props.data?.customers_id || "",
  customer: props.data?.customer || "",
  shift_end_reports_id: props.data?.shift_end_reports_id || "",
  attachment: props.data?.attachment || "",
  reference: props.data?.reference || "",
  status_id: props.data?.status_id || "",
  invoices: props.data?.invoices || [],
  uid: props.data?.uid || "",
  cashier_dutys_id: props.data?.cashier_dutys_id || props.cashierDuty?.id,
  pos_stations_id: props.data?.pos_stations_id || props.posStation?.id,
  action: "",
  accounting: props.data?.accounting || [],
  ledgers: props.data?.ledgers || [],
  download: false,
  remarks: props.data?.remarks || "",
  total_payment_value: props.data?.total_payment_value || 0,
});

setFieldRules("customers_id", [required("Customer is required")]);
setFieldRules("invoices", [required("at least 1 Invoice is required")]);
setFieldRules("payment_types_id", [required("Payment Type is is required")]);
setFieldRules("currencys_id", [required("Currency is is required")]);
setFieldRules("exchange_rate", [required("Exchange Rate is is required")]);
setFieldRules("chart_of_accounts_id", [required("Account is is required")]);
    


const currency = ref<Currency>();
const customer = ref<Customer>();
const payment_type = ref<PaymentType>();

const handleSubmit = async (
  new_record: boolean,
  download: boolean = false,
  mail: boolean = false,
  share: boolean = false,
  print = false
) => {
  if (invoices.value.length === 0 && ledgers.value.length > 0) {
    setFieldRules("customers_id", []);
  } else {
    setFieldRules("customers_id", [required("vendor is required")]);
  }
  if (totalPaymentValue.value <= 0) {
    setFieldRules("payment_types_id", []);
    setFieldRules("chart_of_accounts_id", []);
  }
  if (validateForm()) {
    /*const totalPaymentAmount = invoices.value.reduce(
      (sum, invoice) => Number(sum) + Number(invoice.payment_amount),
      0
    );*/
    if (selectedInvoices.value.size === 0 && ledgers.value.filter(ledger => (ledger.chart_of_accounts_id ?? 0) > 0 && (ledger.amount ?? 0) > 0).length === 0) {
      MessageBox.show("Select at least 1 Invoice/Ledger to proceed", "warning");
      return;
    }
    /*if (totalPaymentValue.value <= 0) {
      MessageBox.show("Payment amount must be greater than zero", "warning");
      return;
    }*/
    if (totalPaymentValue.value != totalSelectedInvoiceDue.value && !isExcessShortValid) {
      MessageBox.show("Total Payment Amount must be equal to Total Selected Invoices Due Amount", "warning");
      return;
    }
    fields.value.amount.value = Number(invoices.value.filter(inv => inv.type === 'Invoice').reduce((sum, inv) => sum + Number(inv.payment_amount || 0), 0));
    fields.value.cashier_dutys_id.value = props.cashierDuty?.id
    fields.value.pos_stations_id.value = props.posStation?.id
    fields.value.outlets_id.value = props.posStation?.outlets_id
    fields.value.invoices.value = invoices.value;
    fields.value.download.value = download || share || print;
    fields.value.total_payment_value.value = totalPaymentValue.value;
    fields.value.ledgers.value = ledgers.value.filter(ledger => (ledger.chart_of_accounts_id ?? 0) > 0 && (ledger.amount ?? 0) > 0);
    fields.value.action.value =
      download || share || print ? "download" : mail ? "mail" : "";
    const response = await api.post(
      (props.endPoint.slice(-1) == "/"
        ? props.endPoint
        : props.endPoint + "/") + "create",
      formData.value
    );
    if (response.success == true) {
      isReadOnly.value = true;
      Toast.show(props.title + " Saved successfully");
      emit("save", null);
      if (new_record) {
        clearForm();
      } else if (download) {
        downloadPDFBlob(response.data.file, 'Payment Receipt#: ' + response.data.data.document_no);
      } else if (mail) {
        if (!response.data.error) {
          Toast.show('Payment Receipt#: ' + response.data.data.document_no + " emailed successfully");
        }
      } else if (share) {
        sharePDFBlob(
          response.data.file, 'Payment Receipt#: ' + response.data.data.document_no
        );
      } else if (print) {
        printPDFBlob(response.data.file);
      }
      //setFormData(response.data.data);
      fields.value.status_id.value = 4;
      fields.value.accounting.value = response.data.data.accounting;
      invoices.value = response.data.data.invoices;
      ledgers.value = response.data.data.ledgers;
      if (invoices.value.length > 0)
      activeTab.value = "invoices";
    else if (ledgers.value.length > 0)
      activeTab.value = "ledgers";
    else activeTab.value = "accounting";

    }
  }
};

const handleActions = async (action: string) => {
  documentActions(props.endPoint, formData.value.uid, action, "Payment Receipt# " + formData.value.document_no);
};

const setData = (data: any) => {
  console.log("setData", data)
  setFormData(data);
  customer.value = data.customer;
  formData.value.date_time = formatDate(data.date_time);
  invoices.value = data.invoices;
  ledgers.value = data.ledgers;

  if (invoices.value.length > 0)
      activeTab.value = "invoices";
    else if (ledgers.value.length > 0)
      activeTab.value = "ledgers";
    else activeTab.value = "accounting";

};

const clearForm = () => {
  resetForm();

  invoices.value = [];
  ledgers.value = [];
  selectedInvoices.value.clear();

  (formData.value.date_time = new Date().toISOString().split("T")[0]),
    (formData.value.prefix = "");

  invoices.value = [];

  for (let i = 0; i < 5; i++) {
    ledgers.value.push({
      uid: "",
      account_name: "",
      account_code: "",
      chart_of_accounts_id: null,
      currencys_id: fields.value.currencys_id.value,
      exchange_rate: fields.value.exchange_rate.value,
      remarks: "",
      debit: 0,
      credit: 0,
      amount: 0
    });

    activeTab.value = "invoices";
  }

  isReadOnly.value = false;
};

const invoices = ref<InvoiceList[]>([]);
const ledgers = ref<GeneralLedger[]>([]);
const selectedInvoices = ref<Set<string>>(new Set());

const toggleInvoiceSelection = (invoice: InvoiceList) => {
  if (selectedInvoices.value.has(String(invoice.document_no))) {
    selectedInvoices.value.delete(String(invoice.document_no));
    invoice.payment_amount = 0;
  } else {
    selectedInvoices.value.add(String(invoice.document_no));
    invoice.payment_amount = invoice.due_amount;
  }

  getPaymentAmount();

};

const handleCustomer = (data: any) => {
  customer.value = data;
  invoices.value = [];
  if (customer.value && !isReadOnly.value) loadInvoices();
};

const handlePaymentType = (data?: PaymentType) => {
  payment_type.value = data;
  if (data?.chart_of_accounts_id)
    fields.value.chart_of_accounts_id.value = data?.chart_of_accounts_id;

  if (data?.require_attachment == true) {
    setFieldRules("attachment", [required("Attachment is required")]);
  } else {
    setFieldRules("attachment", []);
  }
  if (data?.require_reference == true) {
    setFieldRules("reference", [required("Reference is required")]);
  } else {
    setFieldRules("reference", []);
  }
};

const handleChartOfAccount = (data?: any, index?: number) => {
  if (!data || index === undefined) return;
  ledgers.value[index].account_name = data.name;
  ledgers.value[index].account_code = data.code;
  ledgers.value[index].chart_of_accounts_id = data.id
};

const handleCurrency = (data?: Currency) => {
  currency.value = data;
  if (data && data.exchange_rates && data.exchange_rates.length > 0) {
    fields.value.exchange_rate.value = data.exchange_rates[0].rate;    
  } else {
    fields.value.exchange_rate.value = 1;
  }
  updateCurrency(false);
};

const updateCurrency = (isNew: boolean = false) => {
  invoices.value.forEach(inv => {
    if (isNew)  {
      inv.original_due_amount = inv.due_amount;
    }
    inv.due_amount = inv.original_due_amount / fields.value.exchange_rate.value;
  });
  ledgers.value.forEach((ledger) => {
    ledger.currencys_id = fields.value.currencys_id.value;
    ledger.exchange_rate = fields.value.exchange_rate.value;
  });
}

const loadInvoices = async () => {
  if (!customer.value || isEmpty(customer.value?.id)) return;
  const params = {
    id: customer.value?.id,
  };
  const response = await api.post(
    "revenue/sales/sales-invoices/get-invoices",
    params
  );
  if (response.success == true) {
    invoices.value = response.data;
    updateCurrency(true);
  }
};


const cancelReceipt = async () => {
  const confirmed = await MessageBox.confirm({
        title: "Cancel Payment Receipt",
        message:
          "Are you sure you want to cancel this Payment Receipt?",
        confirmText: "Yes",
        cancelText: "No",
        type: "warning",
      });
      if (!confirmed) return;
      const params = {
        id: formData.value.uid,
      };
      const response = await api.post(
        (props.endPoint.slice(-1) == "/"
          ? props.endPoint
          : props.endPoint + "/") + "cancel",
        params
      );
      if (response.success == true) {
        Toast.show("Payment Receipt cancelled successfully");
        isReadOnly.value = true;
        //setData(response.data);
        fields.value.status_id.value = 5;
      }      
}

const getRecord = async () => {
  if (isEmpty(props.data)) return;
  const params = {
    id: props.data.uid,
  };
  const response = await api.post(
    (props.endPoint.slice(-1) == "/" ? props.endPoint : props.endPoint + "/") +
      "record",
    params
  );
  if (response.success == true) {
    setData(response.data);
  }
};

const activeTab = ref("invoices");
const paymentAmount = ref(0);

const handlePaymentSelected = (invoice: InvoiceList) => {
  if (invoice.selected == true)
    invoice.payment_amount = invoice.due_amount;
  else invoice.payment_amount = 0;

  getPaymentAmount();

};

const getPaymentAmount = () => {
  paymentAmount.value = invoices.value
    .filter(inv => selectedInvoices.value.has(String(inv.document_no)))
    .reduce((sum, invoice) => Number(sum) + (invoice.payment_amount > 0 ? invoice.payment_amount : -Math.abs(invoice.payment_amount)), 0);

  const totalLedgerAmount = ledgers.value
    .filter(ledger => ledger.chart_of_accounts_id ?? 0 > 0)
    .reduce((sum, ledger) => sum + (ledger.amount || 0), 0);
  paymentAmount.value += totalLedgerAmount;

};

const currencys = ref<Currency[]>([]);
const chart_of_accounts = ref([]);
const payment_types = ref<PaymentType[]>([]);
const setLists = async (data: any) => {
  if (!data) return;
  currencys.value = data["currencys"];
  chart_of_accounts.value = data["chart_of_accounts"];
  payment_types.value = data["payment_types"];
};

const isReadOnly = ref(true);
const showForm = ref(false);
const excess_account = ref<any>(null);
const max_short_excess_amount = ref(0);
onMounted(() => {
  excess_account.value = tokenManager.getSetting('collection_short_account', true)
  max_short_excess_amount.value = Number(tokenManager.getSetting('maximum_short_excess_limit', true) || 0)
  showForm.value = true;
  isReadOnly.value = props.mode == "view"; 
  setLists(props.lists);
  if (props.mode != "create") {
    getRecord();
  } else {    
    clearForm();
  }
});


const hanldeOcrRead = (reference: string, amount: string) => {
  if (reference)
      fields.value.reference.value = reference
    if (amount) {
      console.log(invoices.value)
      if (invoices.value.length > 0) {
        var bal: number = Number(amount);
        invoices.value.forEach((invoice) => {
          if (bal > 0) {
            if (bal >= Number(invoice.net_total) - Number(invoice.paid_amount)) {
              invoice.payment_amount = invoice.net_total - invoice.paid_amount;
              bal -= invoice.net_total - invoice.paid_amount;
            } else {
              invoice.payment_amount = bal;
              bal = 0;
            }
          }
          console.log(invoice)
        });
      }
    }
}

const addLedgerRows = () => {
  ledgers.value.push({
      uid: "",
      account_name: "",
      account_code: "",
      chart_of_accounts_id: null,
      currencys_id: fields.value.currencys_id.value,
      exchange_rate: fields.value.exchange_rate.value,
      remarks: "",
      debit: 0,
      credit: 0,
      amount: 0
    });
};

const isExcessShortValid = computed(() => {
  return (Math.abs(totalSelectedInvoiceDue.value - totalPaymentValue.value)*fields.value.exchange_rate.value) <= max_short_excess_amount.value;
});

const totalSelectedInvoiceDue = ref(0);
const totalPaymentValue = ref(0);
const updateTotalSelectedInvoiceDue = () => {
  totalSelectedInvoiceDue.value = invoices.value
    .filter(inv => selectedInvoices.value.has(String(inv.document_no)))
    .reduce((sum, invoice) => sum + (invoice.payment_amount || 0), 0);

  const totalLedgerAmount = ledgers.value
    .filter(ledger => ledger.chart_of_accounts_id ?? 0 > 0)
    .reduce((sum, ledger) => sum + (ledger.amount || 0), 0);
  totalSelectedInvoiceDue.value += totalLedgerAmount;

  totalPaymentValue.value = totalSelectedInvoiceDue.value;
};

// Watch for changes in invoices' payment_amount and update totalSelectedInvoiceDue
watch(
  () => invoices.value.map(inv => inv.payment_amount),
  updateTotalSelectedInvoiceDue,
  { deep: true }
);

watch(
  () => ledgers.value.map(inv => inv.amount),
  updateTotalSelectedInvoiceDue,
  { deep: true }
);

watch(
  () => ledgers.value.map(inv => inv.chart_of_accounts_id),
  updateTotalSelectedInvoiceDue,
  { deep: true }
);

watch(() => formData.value.date_time, (newVal) => {
  console.log("Date object changed:", newVal);
});

</script>

<template>
  <ModalFormComponent
    width="55rem"
    :header="false"
    @close="$emit('close')"
  >
  <template #header> 
    <!-- Header -->
    <DocumentHeader
      :title="title"
      :end-point="endPoint"
      :data="formData"
      :readonly="isReadOnly"
      :show-drop-down="false"
      @save="handleSubmit(false)"
        @save-new="handleSubmit(true)"
        @download="handleSubmit(false, true)"
        @mail="handleSubmit(false, false, true)"
        @share="handleSubmit(false, false, false, true)"
        @print="handleSubmit(false, false, false, false, true)"
        @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
        @close="$emit('close')"
      @clear="clearForm"
    >
    <template #header>
      <Buttons icon="Trash2" caption="Cancel" class="bg-red-600 hover:bg-red-700 text-white" @click="cancelReceipt" v-if="isReadOnly && formData.status_id == 4"/>
    </template>
  </DocumentHeader>
</template>
    <template #form>
      
    <!-- Main Content - Scrollable -->
    <div class="flex-1 overflow-y-auto" style="height: calc(100vh - 4rem);">
      <div class="p-6 bg-white dark:bg-gray-800">
        <div class="grid md:grid-cols-2 gap-6">
          <div>
            <div class="grid md:grid-cols-5 gap-6">
              <div class="col-span-3">
                <SearchSelect
                  v-if="!isReadOnly"
                  label="Customer"
                  class="col-span-3"
                  end-point="revenue/master-data/customers"
                  v-model="fields.customers_id.value"
                  :readonly="isReadOnly"
                  :remote="true"
                  :withDetails="true"
                  :error="
                    fields.customers_id.touched ? fields.customers_id.error : null
                  "
                  @blur="fields.customers_id.touched = true"
                  @select="handleCustomer"
                />
                <div
                  v-if="customer || fields.customer.value"
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg"
                >
                  <h4 class="font-medium text-gray-900 dark:text-white">
                    {{ customer?.name }}
                  </h4>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.email }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.mobile }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    {{ customer?.address ?? customer?.building_address }}
                  </p>
                  <p class="text-sm text-gray-600 dark:text-gray-400">
                    <span>TIN: </span> {{ customer?.tax_registration_no }}
                  </p>
                </div>
                <div
                  v-else
                  class="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg text-gray-500 dark:text-gray-400 text-center"
                >
                  No Customer selected
                </div>
              </div>
              <div class="col-span-2">
                <input
                  v-model="fields.date_time.value"
                  :readonly="isReadOnly"
                  type="date"
                  class="w-full px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-700"
                />
                <TextAreaInput
                  v-model="fields.remarks.value"
                  class="mt-4"
                  :readonly="isReadOnly"
                  label="Remarks"
                />
              </div>
            </div>
          </div>
          <div class="grid md:grid-cols-2 gap-6">
            <div class="grid md:grid-cols-4 gap-2 col-span-2">
              <SearchSelect
                label="Currency"
                class="col-span-2"
                v-model="fields.currencys_id.value"
                :readonly="isReadOnly"
                :options="currencys"
                end-point="finance/master-data/currency"
                :withDetails="true"
                :error="
                  fields.currencys_id.touched
                    ? fields.currencys_id.error
                    : null
                "
                @blur="fields.currencys_id.touched = true"
                @select="handleCurrency"
              />
              <AmountInput
                v-model.number="fields.exchange_rate.value"
                label="Ex. Rate"
                :readonly="isReadOnly"
                type="number"
                :error="
                  fields.exchange_rate.touched
                    ? fields.exchange_rate.error
                    : null
                "/>
                <TextInput v-model="fields.document_no.value" label="Ref. No" :readonly="true" />
            </div>
              <SearchSelect
                label="Payment Type"
                v-model="fields.payment_types_id.value"
                :options="payment_types"
                end-point="finance/master-data/payment-types"
                :readonly="isReadOnly || isEmpty(fields.currencys_id.value)"
                remove="credit"
                :withDetails="true"
                :error="
                  fields.payment_types_id.touched
                    ? fields.payment_types_id.error
                    : null
                "
                @blur="fields.payment_types_id.touched = true"
                @select="handlePaymentType"
              />
              <SearchSelect
                label="Bank/Cash Account"
                v-model="fields.chart_of_accounts_id.value"
                :options="chart_of_accounts"
                end-point="finance/accounting/chart-of-accounts"
                extra="Bank,Cash"
                :extra2="fields.currencys_id.value"
                :readonly="isReadOnly"
                :withDetails="true"
                :error="
                  fields.chart_of_accounts_id.touched
                    ? fields.chart_of_accounts_id.error
                    : null
                "
                @blur="fields.chart_of_accounts_id.touched = true"
              />
            <div class="space-y-6 col-span-2">
              <FileUpload
              v-model="fields.attachment.value"
              v-if="payment_type && payment_type?.require_attachment"
              label="Attachment"
              path="payment_receipts"
              :readonly="isReadOnly" 
              :ocr="payment_type.read_ocr"
              :ocr-length="payment_type.ocr_string_length"
              :ocr-match="payment_type.ocr_init_string"
              :currency="currency?.code"
              @ocrRead="hanldeOcrRead"
              :error="
              fields.attachment.touched
                ? fields.attachment.error
                : null
            "
            @blur="fields.attachment.touched = true"
            />
            <TextInput
              v-model="fields.reference.value"
              v-if="payment_type && payment_type?.require_reference"
              label="Reference"
              :readonly="isReadOnly"
              type="textarea"
              :error="
              fields.reference.touched
                ? fields.reference.error
                : null" />
            </div>
          </div>
        </div>

        <!--header-->
        <!-- Tabs -->
        <div
          class="border-b border-gray-200 dark:border-gray-700"
          v-if="!isMobile"
        >
            <TabComponent
            :tabs="[ 
              { name: 'invoices', label: 'Invoices', visible: !isReadOnly || invoices.length > 0 },
              { name: 'ledgers', label: 'Ledgers', visible: !isReadOnly || ledgers.length > 0 },
              { name: 'accounting', label: 'Accounting', visible: isReadOnly },
            ]"
            v-model="activeTab"
          >
          <template #buttons>
            <div class="flex flex-row gap-2">
              <div class="flex items-center bg-green-800 dark:bg-green-400 text-white px-4 py-2 rounded-lg">
                Total Invoice Amount: {{ formatNumber(invoices.filter(inv => inv.type === 'Invoice').reduce((sum, inv) => sum + (inv.payment_amount || 0), 0)) }}
              </div>
                <div class="flex items-center bg-green-800 dark:bg-green-400 text-white px-4 py-2 rounded-lg">
                <span>Total Credit Notes Amount:</span>
                <span class="ml-2">{{
                  formatNumber(
                  Math.abs(
                  invoices
                  .filter(inv => inv.type === 'Credit Note')
                  .reduce((sum, inv) => Number(sum) + Number(inv.payment_amount || 0), 0)
                  )
                  )
                }}</span>
                </div>
              <div class="bg-green-800 dark:bg-green-400 text-white px-4 py-2 rounded-lg" v-if="isReadOnly">
                Total Payment Amount: {{ formatNumber(invoices.reduce((sum, inv) => Number(sum) + Number((inv.type === 'Credit Note' ? inv.payment_amount * -1 : inv.payment_amount) || 0), 0)) }}
              </div>
              <div class="bg-green-800 dark:bg-green-400 text-white px-4 py-2 rounded-lg" v-else>
                <div class="flex items-center gap-2">
                  <span>Total Payment Amount:</span>
                  <AmountInput v-model.number="totalPaymentValue" :readonly="isReadOnly" class="bg-green-800 dark:bg-green-400 text-white px-4 py-2 rounded-lg"/>
                </div>
              </div>
              <div class="flex items-center bg-orange-400 dark:bg-orange-200 text-black px-4 py-2 rounded-lg" v-if="totalPaymentValue != totalSelectedInvoiceDue">
                <div class="flex items-center gap-2" v-if="!isEmpty(excess_account) && isExcessShortValid">
                  Excess/Short Amount: {{ formatNumber((totalPaymentValue - totalSelectedInvoiceDue) * fields.exchange_rate.value) }}
                </div>
                <div v-else-if="!isEmpty(excess_account)" class="flex items-center gap-2">
                  <div>
                  <span>Excess/Short can be maximum of MVR {{ formatNumber(max_short_excess_amount) }}.</span> <br/>
                  <span>Current Excess/Short is MVR {{ formatNumber((totalPaymentValue - totalSelectedInvoiceDue) * fields.exchange_rate.value) }}.</span>
                  </div>
                  </div>
                  <div v-else="" class="flex items-center gap-2">
                  <span>Excess Short Account has to be defined in the settings.</span>
              </div>
            </div>
            </div>
          </template>
            <template #invoices>
              <table class="w-full">
                <thead class="bg-white dark:bg-gray-900">
                  <tr>
                    <th
                      class="sticky left-0 w-[250px] z-20 px-0 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Invoice No
                    </th>
                    <th
                      class="px-0 py-3 w-[180px] text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Date
                    </th>
                    <th
                      class="px-0 py-3 w-[180px] text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Due Date
                    </th>
                    <!--<th class="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap">Item Type</th>-->
                    <th
                      class="px-0 py-3 w-[150px] text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Invoice
                    </th>
                    <th
                      class="px-0 py-3 w-[150px] text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Paid
                    </th>
                    <th
                      class="px-0 py-3 w-[150px] text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Due ({{currency ? currency.code : 'MVR'}})
                    </th>
                    <th
                      class="px-0 py-3 w-[150px] text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Payment
                    </th>
                    <!-- Fixed right column -->
                    <th
                    v-if="!isReadOnly"
                      class="sticky right-0 bg-white dark:bg-gray-900 z-20 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    ></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <template
                    v-for="invoice in invoices"
                    :key="invoice.id"
                    v-if="invoices?.length > 0"
                  >
                    <tr>
                      <!-- Fixed left column -->
                      <td class="sticky left-0 z-10 px-0 py-3">
                        {{ invoice.type + ' - ' + invoice.document_no }}
                      </td>
                      <td class="px-0 py-3">
                        {{ formatDate(invoice.date_time) }}
                      </td>
                      <td class="px-0 py-3">
                        {{ formatDate(invoice.due_date) }}
                      </td>
                      <td class="px-0 py-3 text-right">
                        {{ formatNumber(invoice.net_total) }}
                      </td>
                      <td class="px-0 py-3 text-right">
                        {{ formatNumber(invoice.paid_amount) }}
                      </td>
                      <td class="px-0 py-3 text-right">
                        {{
                          formatNumber(invoice.due_amount)
                        }}
                      </td>
                      <td class="text-right">
                        <AmountInput
                          v-if="!isReadOnly"
                          :id="'invoice_' + invoice.document_no"
                          v-model.number="invoice.payment_amount"
                          :max="invoice.due_amount"
                          type="number"
                          :min="0"
                          class="px-0 py-2 rounded-lg border-gray-200 dark:border-gray-700 text-right"
                        />
                        <span v-else>{{
                          formatNumber(invoice.payment_amount)
                        }}</span>
                      </td>
                      <td
                        v-if="!isReadOnly"
                        class="sticky right-0 w-10 bg-white dark:bg-gray-900 z-10 px-4 py-3 text-right font-medium"
                      >
                        <input
                          type="checkbox"
                          :checked="
                            selectedInvoices.has(String(invoice.document_no))
                          "
                          :style="{
                            pointerEvents: isReadOnly ? 'none' : 'auto',
                          }"
                          @change="toggleInvoiceSelection(invoice)"
                        />
                      </td>
                    </tr>
                  </template>
                  <tr v-else>
                    <td class="w-full text-center p-6" colspan="7">
                      <span v-if="!customer"
                        >Select a Customer to view Pending Invoices
                      </span>
                      <span v-else>There are no Pending Invoices ....</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </template>
            <template #ledgers v-if="activeTab == 'ledgers'">
              <table class="w-full">
                <thead class="bg-white dark:bg-gray-900">
                  <tr>
                    <th
                      class="sticky left-0 w-[100px] z-20 px-0 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Account
                    </th>
                    <th
                      class="px-0 py-3 w-[50px] text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Amount
                    </th>
                    <th
                      class="px-0 py-3 w-[150px] text-left pl-5  text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    >
                      Remarks
                    </th>
                    <th
                    v-if="!isReadOnly"
                      class="sticky right-0 bg-white dark:bg-gray-900 z-20 px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase whitespace-nowrap"
                    ><Buttons caption="Add Row" v-if="!isReadOnly" icon="Plus" @click="addLedgerRows" /></th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
                  <template
                    v-for="(ledger, lIndex) in ledgers"
                    :key="ledger.id"
                    v-if="!isReadOnly || ledgers?.length > 0"
                  >
                    <tr>
                      <!-- Fixed left column -->
                      <td class="left-0 z-10 px-0 py-3">
                        <SearchSelect
                          label=""
                          v-if="!isReadOnly"
                          v-model="ledger.chart_of_accounts_id"
                          :options="chart_of_accounts"
                          :return-value="lIndex"
                          @select="handleChartOfAccount"
                        />
                        <span v-else>{{ ledger.account_code }} - {{ ledger.account_name }}</span>
                      </td>
                      <td class="text-right">
                        <AmountInput
                          v-if="!isReadOnly"
                          :id="'invoice_' + ledger.account_code"
                          v-model.number="ledger.amount"
                          @input="getPaymentAmount"
                          type="number"
                          :min="0"
                          class="px-0 py-2 rounded-lg border-gray-200 dark:border-gray-700 text-right"
                        />
                        <span v-else>{{
                          formatNumber(ledger.amount)
                        }}</span>
                      </td>
                      <td class="px-0 py-3 pl-5">
                        <TextInput v-model="ledger.remarks" :readonly="isReadOnly" class="w-full" v-if="!isReadOnly" label=""/>
                        <span v-else>{{ ledger.remarks }}</span>
                      </td>
                      <td
                        v-if="!isReadOnly"
                        class="sticky right-0 w-10 bg-white dark:bg-gray-900 z-10 px-4 py-3 text-right font-medium"
                      >
                      </td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </template>
            <template #accounting v-if="mode != 'create' && activeTab == 'accounting'">
              <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
            </template>
          </TabComponent>
        </div>
        <template v-if="isMobile">
          <h2 class="font-medium text-gray-900 dark:text-white p-4">
            INVOICES
          </h2>
          <hr class="p-4" />
          <template
            v-for="invoice in invoices"
            :key="invoice.id"
            v-if="invoices?.length > 0"
          >
            <div
              class="bg-white dark:bg-gray-900 rounded-lg shadow p-4 border border-gray-200 dark:border-gray-700 mb-2"
            >
              <div class="flex justify-between items-start">
                <span
                  class="text-gray-500 dark:text-gray-400 mb-3"
                  v-if="isReadOnly"
                  >Invoice No:</span
                >
                <h3 class="font-medium text-gray-900 dark:text-white">
                  {{ invoice.document_no }}
                </h3>
                <RadioCheckInput
                  v-if="!isReadOnly"
                  v-model="invoice.selected"
                  @checked="handlePaymentSelected(invoice)"
                />
              </div>

              <div class="space-y-2 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-500 dark:text-gray-400"
                    >Invoice Date</span
                  >
                  <span class="font-medium">{{
                    formatDate(invoice.date_time)
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500 dark:text-gray-400">Due Date</span>
                  <span class="font-medium">{{
                    formatDate(invoice.due_date)
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500 dark:text-gray-400"
                    >Invoice Amount</span
                  >
                  <span class="font-medium">{{
                    formatNumber(invoice.net_total)
                  }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500 dark:text-gray-400"
                    >Due Amount</span
                  >
                  <span class="font-medium">{{
                    formatNumber(invoice.net_total - invoice.paid_amount)
                  }}</span>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <div class="flex justify-between">
              <span v-if="!customer"
                >Select a Customer to view Pending Invoices
              </span>
              <span v-else>There are no Pending Invoices ....</span>
            </div>
          </template>
          <template v-if="isReadOnly">
            <h2 class="font-medium text-gray-900 dark:text-white p-4">
              ACCOUNTING
            </h2>
            <AccountingLog :data="formData.accounting" v-if="isReadOnly" />
          </template>
        </template>
      </div>
    </div>

    </template>

    <!-- Footer -->
    <template #footer>
    <DocumentFooter
      :data="formData"
      :readonly="isReadOnly"
      :end-point="endPoint"
      :permissions="permissions"
      :title="title"
      convert-module="Debit Note"
      @close="$emit('close')"
      @save="handleSubmit(false)"
      @save-new="handleSubmit(true)"
      @download="handleSubmit(false, true)"
      @new="clearForm"
      @mail="handleSubmit(false, false, true)"
      @share="handleSubmit(false, false, false, true)"
      @print="handleSubmit(false, false, false, false, true)"
      @view-print="handleActions('print')"
        @view-share="handleActions('share')"
        @view-download="handleActions('download')"
        @view-mail="handleActions('mail')"
    />
    </template>
  </ModalFormComponent>
</template>
